'use client';

import { useAuthSessionHint } from '@/hooks/auth/use-has-auth-session-hint';
import { useEffect } from 'react';
import { preloadPageBoundWorkspaceRuntime } from './site-runtime-page-bound-workspace-host';

export function SiteRuntimeWorkspacePreloader({
  enabled,
  locale,
  toolId,
  storageUploadOrigin,
}: {
  enabled: boolean;
  locale: string;
  toolId: string | null;
  storageUploadOrigin?: string;
}) {
  const { hasAuthSessionHint } = useAuthSessionHint({ enabled });

  useEffect(() => {
    if (!storageUploadOrigin) {
      return;
    }

    let idleCallbackId: number | undefined;
    let timeoutId: number | undefined;
    const preconnect = () => {
      if (
        document.querySelector('link[data-workspace-storage-upload-preconnect]')
      ) {
        return;
      }

      const link = document.createElement('link');
      link.rel = 'preconnect';
      link.href = storageUploadOrigin;
      link.crossOrigin = 'anonymous';
      link.dataset.workspaceStorageUploadPreconnect = '';
      document.head.appendChild(link);
    };
    const scheduleAfterLoad = () => {
      if (typeof window.requestIdleCallback === 'function') {
        idleCallbackId = window.requestIdleCallback(preconnect);
        return;
      }
      timeoutId = window.setTimeout(preconnect, 0);
    };

    if (document.readyState === 'complete') {
      scheduleAfterLoad();
    } else {
      window.addEventListener('load', scheduleAfterLoad, { once: true });
    }

    return () => {
      window.removeEventListener('load', scheduleAfterLoad);
      if (idleCallbackId !== undefined) {
        window.cancelIdleCallback(idleCallbackId);
      }
      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [storageUploadOrigin]);

  useEffect(() => {
    if (!enabled || !hasAuthSessionHint || !toolId) {
      return;
    }

    void preloadPageBoundWorkspaceRuntime({ toolId, locale }).catch(() => {});
  }, [enabled, hasAuthSessionHint, locale, toolId]);

  return null;
}
